// API-connected data service.
// The public site can render in two modes decided at runtime from the URL:
//  - legacy single-tenant public site (/paelbi...) -> /api/public/{SLUG}/...
//  - Guest Portal (/g/{token}...) -> /api/guest/{token}/... (PIN-gated, or owner preview)
import axios from 'axios';
import { tenant as tenantConfig, domiraBrand, guestNav, domiraNav, footerLinks } from '../data/config';
import { publicSlugFromPath } from '../lib/tenantPath';
import { API_BASE } from '../lib/apiBase';

const API = API_BASE;

const http = axios.create({ baseURL: API, timeout: 20000 });

let currentLang = 'it';
export const setLanguage = (lang) => { currentLang = lang; };
export const getLanguage = () => currentLang;

export function guestTokenFromPath() {
  const m = window.location.pathname.match(/^\/g\/([^/]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export function isGuestPreview(token) {
  return !!sessionStorage.getItem(`guest_preview_${token}`);
}

function guestAuth() {
  // Guest session travels in the httpOnly `guest_session` cookie (or the admin
  // cookie for owner preview); no token is exposed to JavaScript. Just send creds.
  return { headers: {}, withCredentials: true };
}

// Fetch tenant-scoped content, auth-gated when in guest mode.
async function getContent(pathSuffix) {
  const gt = guestTokenFromPath();
  if (gt) {
    const { headers, withCredentials } = guestAuth();
    const { data } = await http.get(`/guest/${gt}/${pathSuffix}?lang=${currentLang}`, { headers, withCredentials });
    return data;
  }
  const { data } = await http.get(`/public/${publicSlugFromPath(window.location.pathname)}/${pathSuffix}?lang=${currentLang}`);
  return data;
}

async function getPlain(path) {
  const sep = path.includes('?') ? '&' : '?';
  const { data } = await http.get(`${path}${sep}lang=${currentLang}`);
  return data;
}

// Record a QR scan once per browser session (property via slug, guest via token).
export async function recordScan({ slug, token, source }) {
  const key = `qr_scanned_${source}_${token || slug}`;
  if (sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, '1');
  try { await http.post('/scan', { slug, token, source }); } catch { /* scan is fire-and-forget; ignore network errors */ }
}

export const dataService = {
  getTenant: () => Promise.resolve(tenantConfig),
  getDomiraBrand: () => Promise.resolve(domiraBrand),
  getNavigation: () => Promise.resolve({ guestNav, domiraNav, footerLinks }),

  getConfig: () => {
    const gt = guestTokenFromPath();
    return gt ? getPlain(`/guest/${gt}/config`) : getPlain(`/public/config?slug=${publicSlugFromPath(window.location.pathname)}`);
  },
  getDomiraHome: () => getPlain(`/public/domira-home`),
  getGuestHome: () => getContent('guest-home'),
  getExperiences: () => getContent('experiences'),
  getEvents: () => getContent('events'),
  getNearby: () => getContent('nearby'),
  getArtMuseums: () => getContent('art-museums'),
  getServices: () => getContent('services'),
  getInfoBnb: () => getContent('info-bnb'),
};

export default dataService;
